import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {loadDesignGallery} from '@Libraries/poster-library';
import type {DesignGalleryProps, GalleryDesignClickedEventDetail} from '@Components/design-gallery/design-gallery.types';
import {GALLERY_CREATE_WITH_AI_CLICKED_EVENT, GALLERY_DESIGN_CLICKED_EVENT, GALLERY_UPLOAD_MEDIA_CLICKED_EVENT, GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import styles from './design-gallery.module.scss';
import type {DesignSelection} from '@Components/social-media/social-media.types';

export function DesignGallery({
  onDesignsSelected,
  onUploadMediaClick,
  onCreateWithAiClick,
  disableCreateFromScratchCTA = true,
  height = '80vh',
  startingPageURI = GalleryThemeURI.DEFAULT,
}: DesignGalleryProps): ReactElement | null {
  const [isGalleryLoaded, setIsGalleryLoaded] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const onUploadMediaClickRef = useRef(onUploadMediaClick);
  const onCreateWithAiClickRef = useRef(onCreateWithAiClick);

  useEffect(() => {
    onUploadMediaClickRef.current = onUploadMediaClick;
    onCreateWithAiClickRef.current = onCreateWithAiClick;
  });

  const loadDesignGalleryInElement = async (): Promise<void> => {
    await loadDesignGallery({
      contentElementSelector: '.js-design-gallery-embedded-content',
      contentContainerSelector: '.js-embedded-gallery-container',
      disableCreateFromScratchCTA,
      startingPageURI,
      showUploadMediaTile: onUploadMediaClick !== undefined,
    });
  };

  const onGalleryDesignClicked = (event: CustomEvent<GalleryDesignClickedEventDetail>): void => {
    const posterHashedId = event.detail.id;
    const pageHashedId = event.detail.idPage;

    if (!posterHashedId || !pageHashedId) {
      return;
    }

    const designSelection: DesignSelection = {
      posterHashedId: posterHashedId,
      pageHashedId: pageHashedId,
    };
    onDesignsSelected([designSelection]);
  };

  const claimGalleryClickForHost = (event: Event, hostClickHandler: VoidFunction | undefined): void => {
    if (!hostClickHandler) {
      return;
    }

    event.preventDefault();
    hostClickHandler();
  };

  const onGalleryUploadMediaClicked = (event: Event): void => {
    claimGalleryClickForHost(event, onUploadMediaClickRef.current);
  };

  const onGalleryCreateWithAiClicked = (event: Event): void => {
    claimGalleryClickForHost(event, onCreateWithAiClickRef.current);
  };

  useEffect(() => {
    if (!isGalleryLoaded) {
      void loadDesignGalleryInElement().then((): void => {
        setIsGalleryLoaded(true);
      });
    }

    window.addEventListener(GALLERY_DESIGN_CLICKED_EVENT, onGalleryDesignClicked as EventListener);
    window.addEventListener(GALLERY_UPLOAD_MEDIA_CLICKED_EVENT, onGalleryUploadMediaClicked);
    window.addEventListener(GALLERY_CREATE_WITH_AI_CLICKED_EVENT, onGalleryCreateWithAiClicked);

    return (): void => {
      window.removeEventListener(GALLERY_DESIGN_CLICKED_EVENT, onGalleryDesignClicked as EventListener);
      window.removeEventListener(GALLERY_UPLOAD_MEDIA_CLICKED_EVENT, onGalleryUploadMediaClicked);
      window.removeEventListener(GALLERY_CREATE_WITH_AI_CLICKED_EVENT, onGalleryCreateWithAiClicked);
    };
  }, []);

  return (
    <div ref={parentRef}>
      <div className={`js-embedded-gallery-container gallery-content-container ${styles.galleryContentContainer} ${styles.galleryScrollStyles}`} style={{height}}>
        <div className={`content  ${styles.mainContentContainer}`}>
          <div className={`js-loader-container ${styles.loaderBackground}`} />
          <div className={`js-design-gallery-embedded-content ${styles.galleryContent}`} />
        </div>
      </div>
    </div>
  );
}
